import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import Button from '../../components/ui/Button';
import HandoverItemsEditor, { parseHandoverItems } from '../../components/HandoverItemsEditor';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 18px 22px;
  background: #fff;
  border-bottom: 1px solid #E8ECF0;
`;

const Title = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 4px;
`;

const Body = styled.div`
  padding: 18px 22px;
  overflow-y: auto;
  flex: 1;
`;

const Hint = styled.div`
  background: rgba(2, 145, 239, 0.06);
  border: 1px solid rgba(2, 145, 239, 0.2);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const FooterBar = styled.div`
  padding: 14px 22px;
  background: #fff;
  border-top: 1px solid #E8ECF0;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const Spinner = styled(Loader2)`
  animation: ${spin} 0.9s linear infinite;
  color: ${({ theme }) => theme.colors.primary.main};
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px;
`;

const EDITABLE_STATUSES = ['draft', 'rejected'];

export default function HandoverEditorModal({
  offboardingId,
  personFullName,
  onClose,
  getForOffboarding,
  saveItems,
  submit,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('draft');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const plan = await getForOffboarding(offboardingId);
      if (cancelled) return;
      setStatus(plan?.status || 'draft');
      setItems(parseHandoverItems(plan?.itemsJson));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [offboardingId, getForOffboarding]);

  const editable = EDITABLE_STATUSES.includes(status);

  const runSave = async () => {
    setBusy(true);
    const ok = await saveItems(offboardingId, JSON.stringify(items));
    setBusy(false);
    if (ok) onClose();
  };

  const runFillAndSubmit = async () => {
    setBusy(true);
    const saved = await saveItems(offboardingId, JSON.stringify(items));
    const submitted = saved ? await submit(offboardingId) : false;
    setBusy(false);
    // Only dismiss when BOTH steps succeeded; otherwise keep the modal open so
    // the leaver's items aren't lost and the (translated) error toast is actionable.
    if (saved && submitted) onClose();
  };

  return (
    <Overlay onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <Card>
        <Header>
          <Title>
            {editable ? t('offboardingHandover.modal.editTitle') : t('offboardingHandover.modal.viewTitle')}
          </Title>
          <Sub>{personFullName}</Sub>
        </Header>

        <Body>
          {loading ? (
            <Center><Spinner size={28} /></Center>
          ) : (
            <>
              {editable && <Hint>{t('offboardingHandover.modal.fillHint')}</Hint>}
              <HandoverItemsEditor
                items={items}
                onChange={setItems}
                editable={editable}
                emptyText={t('offboardingHandover.modal.empty')}
              />
            </>
          )}
        </Body>

        <FooterBar>
          {editable ? (
            <>
              <Button variant="ghost" disabled={busy} onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button variant="outline" disabled={busy || loading} onClick={runSave}>
                {busy ? <Loader2 size={16} /> : t('offboardingHandover.action.saveDraft')}
              </Button>
              <Button variant="primary" disabled={busy || loading || items.length === 0} onClick={runFillAndSubmit}>
                {busy ? <Loader2 size={16} /> : t('offboardingHandover.action.fillAndSubmit')}
              </Button>
            </>
          ) : (
            <Button variant="primary" disabled={busy} onClick={onClose}>
              {t('offboardingHandover.action.close')}
            </Button>
          )}
        </FooterBar>
      </Card>
    </Overlay>
  );
}
